import { Injectable } from '@angular/core';
import { BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { Version } from './_interfaces/version.interface';
import { QuestionConfig, QuestionGroup, QuestionTypeConfig, QuestionTypeEnum, ValuesGlobalization } from '../shared/interfaces/process-config.interface';
import { ConfigTranslation } from '../shared/interfaces/globalization-config.interface';
import { FilterItem } from '../shared/modals/filters/models/filter-models';

@Injectable()
export class ProcessesService {

    constructor(private _httpService: BackendService) {
    }

    public getProcesses(): Observable<any> {
        return this._httpService.get('getProcesses');
    }

    public getProcessById(processId: number): Observable<any> {
        return this._httpService.get('getProcessById', [], [
            { 'name': 'id', 'value': processId as any }
        ]);
    }

    public addProcess(version: Version): Observable<any> {
        return this._httpService.post('addProcess', version);
    }

    public updateProcess(version: Version): Observable<any> {
        return this._httpService.put('updateProcess', version);
    }

    public deleteProcess(processId: number): Observable<any> {
        return this._httpService.delete('deleteProcess', [
            { 'name': 'id', 'value': processId as any }
        ]);
    }

    public getSelfEvaluationVersions(): Observable<any> {
        return this._httpService.get('getSelfEvaluationVersions', [], []);
    }

    public getValidationVersions(): Observable<any> {
        return this._httpService.get('getValidationVersions', [], []);
    }

    public getSelectionVersions(): Observable<any> {
        return this._httpService.get('getSelectionVersions', [], []);
    }

    public getGoalInputDefaultQuestions(): Observable<any> {
        return this._httpService.get('getGoalInputDefaultQuestions', [], []);
    }

    public getQuestionTypeConfigs(): Observable<any> {
        return this._httpService.get('getQuestionTypeConfigs', [], []);
    }

    public getAllAttributesByType(): Observable<any> {
        return this._httpService.get('getAllAttributesByType', [], []);
    }

    public addQuestionTypeConfig(config: QuestionTypeConfig): Observable<any> {
        return this._httpService.post('addQuestionTypeConfig', config);
    }

    public addQuestionGroup(language: string, processId: number, name: string): Observable<any> {
        const data = {
            language: language,
            processId: processId,
            name: name
        };
        return this._httpService.post('addQuestionGroup', data);
    }

    public updateQuestionGroup(language: string, groupId: number, name: string): Observable<any> {
        const data = {
            language: language,
            groupId: groupId,
            name: name
        };
        return this._httpService.put('updateQuestionGroup', data);
    }

    public updateQuestionGroupAttributes(questionGroupId: number, attributeIds: number[]): Observable<any> {
        const data = {
            questionGroupId,
            attributeIds
        };
        return this._httpService.put('updateQuestionGroupAttributes', data);
    }

    public updateQuestionAttributes(questionId: number, attributeIds: number[]): Observable<any> {
        const data = {
            questionId,
            attributeIds
        };
        return this._httpService.put('updateQuestionAttributes', data);
    }

    public validateQuestions(version: Version): Array<string | number> | null {
        if (!version.versionName) {
            return ['manageVersion.missingName'];
        }

        for (let index = 0; version.questions !== null && index < version.questions.length; index++) {
            const validation = this._validateQuestion(version.questions[index]);
            if (validation)
                return validation;
        }

        if (version.questionGroups && version.questionGroups.length > 0) {
            for (let index = 0; index < version.questionGroups.length; index++) {
                const group = version.questionGroups[index];
                const groupValidation = this._validateQuestionGroup(group);
                if (groupValidation) {
                    groupValidation.push(index + 1);
                    return groupValidation;
                }
                if (group.questions) {
                    for (let j = 0; j < group.questions.length; j++) {
                        const validation = this._validateQuestion(group.questions[j], group.order);
                        if (validation)
                            return validation;
                    }
                }
            }
        }

        if (version.managerQuestionGroups && version.managerQuestionGroups.length > 0) {
            for (let index = 0; index < version.managerQuestionGroups.length; index++) {
                const group = version.managerQuestionGroups[index];
                const groupValidation = this._validateQuestionGroup(group);
                if (groupValidation) {
                    groupValidation.push(index + 1);
                    return groupValidation;
                }
                if (group.questions) {
                    for (let j = 0; j < group.questions.length; j++) {
                        const validation = this._validateQuestion(group.questions[j], group.order);
                        if (validation)
                            return validation;
                    }
                }
            }
        }

        return null;
    }

    public addValidationProcessConfig(versionConfig: Version, isSelectionVersion: boolean = false) {
        return this._httpService.post('addProcessConfig', { 'versionConfig': versionConfig, 'isSelectionVersion': isSelectionVersion });
    }

    public updateValidationProcessConfig(versionConfig: Version, isSelectionVersion: boolean = false) {
        return this._httpService.put('updateProcessConfig', { 'versionConfig': versionConfig, 'isSelectionVersion': isSelectionVersion });
    }

    public cloneProcess(processId: number) {
        return this._httpService.post('cloneProcess', { 'processId': processId });
    }

    private _validateQuestionGroup(group: QuestionGroup): Array<string | number> | null {
        if (!group
            || !group.globalizationConfig['en-US'] || !group.globalizationConfig['en-US'].title
            || !group.globalizationConfig['pt-BR'] || !group.globalizationConfig['pt-BR'].title
            || !group.globalizationConfig['es-ES'] || !group.globalizationConfig['es-ES'].title)
            return ['manageQuestionGroup.missingTranslation'];
        return null;
    }

    private _validateQuestion(question: QuestionConfig, groupNumber?: number): Array<string | number> | null {
        const globConfig = question.globalizationConfig;
        const typeConfig = question.typeConfig;

        if (!typeConfig || !typeConfig.type) {
            return ['manageVersion.missingType', question.order, groupNumber];
        }

        if (!globConfig ||
            !this._isTranslationFilled(globConfig['en-US'], typeConfig.type) ||
            !this._isTranslationFilled(globConfig['pt-BR'], typeConfig.type) ||
            !this._isTranslationFilled(globConfig['es-ES'], typeConfig.type)
        ) {
            return ['manageVersion.missingTranslation', question.order, groupNumber];
        }

        var mustTypeConfigHaveMinLength = typeConfig.type === QuestionTypeEnum.Text ||
            typeConfig.type === QuestionTypeEnum.Title ||
            typeConfig.type === QuestionTypeEnum.Description;
        if (mustTypeConfigHaveMinLength) {
            if (!typeConfig.max) {
                return ['manageVersion.missingMaxLength', question.order, groupNumber];
            } else if (isNaN(typeConfig.max) || typeConfig.max < 1) {
                return ['manageVersion.invalidMaxLength', question.order, groupNumber];
            }
        }

        if (
            typeConfig.type === QuestionTypeEnum.Boolean ||
            typeConfig.type === QuestionTypeEnum.Selection ||
            typeConfig.type === QuestionTypeEnum.Slider
        ) {
            if (!this._arePossibleValuesTranslated(typeConfig.possibleValuesGlobalized)) {
                return ['manageVersion.missingTranslation', question.order, groupNumber];
            }
        }
        return null;
    }

    private _isTranslationFilled(
        translation: ConfigTranslation, type: QuestionTypeEnum
    ): boolean {
        return type === QuestionTypeEnum.Slider || type === QuestionTypeEnum.Boolean ? (
            translation.title !== null && translation.title.trim() !== ''
        ) : (
            translation.title !== null && translation.title.trim() !== '' &&
            translation.placeholder !== null && translation.placeholder.trim() !== ''
        );
    }

    private _arePossibleValuesTranslated(values: ValuesGlobalization): boolean {
        return values &&
            values['pt-BR'].length > 0 &&
            values['pt-BR'].every(t => t.trim() !== '') &&
            values['es-ES'].length > 0 &&
            values['es-ES'].every(t => t.trim() !== '') &&
            values['en-US'].length > 0 &&
            values['en-US'].every(t => t.trim() !== '');
    }

}
